import React, { useState, useEffect } from 'react';
import {
    Box, Button, IconButton, Tooltip, Typography, MenuItem,
    Dialog, DialogTitle, DialogContent, DialogActions, Select,
    TextField, CircularProgress, useMediaQuery, Snackbar, Alert, Switch
} from '@mui/material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { FiArrowLeft, FiPlus, FiEye, FiEdit, FiTrash2, FiHelpCircle } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import Swal from 'sweetalert2';
import api from '../../api';
import QuestionFormModal from '../Admin-Dashboard-Component/FormModalsQuickAdminActions/QuestionFormModal';
import EditQuestionFormModal from '../Admin-Dashboard-Component/FormModalsQuickAdminActions/EditQuestionFormModal';

const QuestionList = () => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editData, setEditData] = useState({ questionText: '', correctOption: '' });
    const [toastOpen, setToastOpen] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [editingQuestion, setEditingQuestion] = useState(null);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);


    const fetchQuestions = async () => {
        try {
            console.log("📡 Fetching questions...");
            const res = await api.get('/questions');
            console.log("✅ Raw API Response:", res);

            // Sometimes the questions are inside res.data.data (Laravel, etc.)
            const questionsData = Array.isArray(res.data)
                ? res.data
                : res.data.data || [];

            console.log("📦 Extracted Questions Data:", questionsData);


            const formatted = res.data.map(q => {
                const isCheckbox = q.question_type === 'MCQ_CHECKBOX';

                return {
                    id: q.id,
                    questionText: q.question_text,
                    correctOption: isCheckbox
                        ? (q.correct_option_array && q.correct_option_array.length > 0
                            ? q.correct_option_array.join(', ')
                            : '-')
                        : (q.correct_option || '-'),

                    status: q.status ? 'Active' : 'Inactive',
                    questionType: q.question_type,
                    marks: q.marks
                };
            });
            console.log('Formatted Questions:', formatted);


            setQuestions(formatted);
        } catch (err) {
            console.error('Fetch failed:', err);
        } finally {
            setLoading(false);
        }
    };

    //for handle toggle status from action column
    const handleStatusToggle = async (id, currentStatus) => {
        const newStatus = currentStatus === 'Active' ? 0 : 1;

        try {
            await api.put(`/questions/${id}`, { status: newStatus });

            setQuestions(prev =>
                prev.map(q => q.id === id ? { ...q, status: newStatus ? 'Active' : 'Inactive' } : q)
            );

            Swal.fire({
                icon: 'success',
                title: `Status changed to ${newStatus ? 'Active' : 'Inactive'}`,
                showConfirmButton: false,
                timer: 1500
            });

        } catch {
            Swal.fire('Error', 'Failed to update status', 'error');
        }
    };


    //function for handling that add question
    const handleAddQuestion = async (newQuestion) => {
        try {
            const isCheckbox = newQuestion.question_type === "MCQ_CHECKBOX";
            const payload = {
                exam_id: newQuestion.exam_id,
                subject_id: newQuestion.subject_id ? parseInt(newQuestion.subject_id) : null,
                topic_id: newQuestion.topic_id ? parseInt(newQuestion.topic_id) : null,
                question_text: newQuestion.question_text,
                question_type: newQuestion.question_type,
                option_a: newQuestion.option_a,
                option_b: newQuestion.option_b,
                option_c: newQuestion.option_c,
                option_d: newQuestion.option_d,
                language_code: newQuestion.language_code || "EN",
                marks: parseInt(newQuestion.marks) || 1,
                ...(isCheckbox
                    ? {
                        correct_option_array: Array.isArray(newQuestion.correct_option)
                            ? newQuestion.correct_option.map(opt => opt.toUpperCase())
                            : [],

                    }
                    : { correct_option: newQuestion.correct_option.toUpperCase() || '' }
                )

            };

            console.log("Final Payload Being Sent:", payload);

            const response = await api.post('/questions', payload);
            console.log("Question added:", response.data);

            if (response.status === 201 || response.status === 200) {
                Swal.fire("Success", "Question added successfully!", "success");
                fetchQuestions();
                setIsAddModalOpen(false);
            } else {
                Swal.fire("Error", "Failed to add question!", "error");
            }
        } catch (error) {
            console.error("API Error:", error?.response?.data || error.message);
            Swal.fire("Error", "An error occurred while adding question.", "error");
        }
    };


    //delete handler function
    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'This question will be deleted!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                await api.delete(`/questions/${id}`);
                setQuestions(prev => prev.filter(q => q.id !== id));
                Swal.fire('Deleted!', 'Question deleted.', 'success');
            } catch {
                Swal.fire('Error', 'Deletion failed.', 'error');
            }
        }
    };

    // const handleEdit = (row) => {
    //     setEditData({ ...row });
    //     setEditModalOpen(true);
    // };

    const handleEdit = (question) => {
        setEditingQuestion(question);
        setIsEditModalOpen(true);
    };

    const handleEditQuestion = async (updatedQuestion) => {
        try {
            const form = new FormData();

            form.append('exam_id', updatedQuestion.exam_id);
            form.append('subject_id', updatedQuestion.subject_id);
            form.append('topic_id', updatedQuestion.topic_id);
            form.append('question_text', updatedQuestion.question_text);
            form.append('question_type', updatedQuestion.question_type);
            form.append('option_a', updatedQuestion.option_a);
            form.append('option_b', updatedQuestion.option_b);
            form.append('option_c', updatedQuestion.option_c);
            form.append('option_d', updatedQuestion.option_d);
            form.append('language_code', updatedQuestion.language_code);
            form.append('marks', updatedQuestion.marks);

            if (updatedQuestion.question_type === 'MCQ_CHECKBOX') {
                if (Array.isArray(updatedQuestion.correct_option_array)) {
                    updatedQuestion.correct_option_array.forEach(opt => {
                        form.append('correct_option_array[]', opt.toUpperCase());
                    });
                }
            } else {
                form.append('correct_option', updatedQuestion.correct_option?.toUpperCase());
            }

            if (updatedQuestion.question_image instanceof File) {
                form.append('question_image', updatedQuestion.question_image);
            }
            if (updatedQuestion.option_a_image instanceof File) {
                form.append('option_a_image', updatedQuestion.option_a_image);
            }
            if (updatedQuestion.option_b_image instanceof File) {
                form.append('option_b_image', updatedQuestion.option_b_image);
            }
            if (updatedQuestion.option_c_image instanceof File) {
                form.append('option_c_image', updatedQuestion.option_c_image);
            }
            if (updatedQuestion.option_d_image instanceof File) {
                form.append('option_d_image', updatedQuestion.option_d_image);
            }

            await api.post(`/questions/${updatedQuestion.id}?_method=PUT`, form, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });


            Swal.fire('Success', 'Question updated successfully!', 'success');
            setIsEditModalOpen(false);
            fetchQuestions();
        } catch (error) {
            console.error('Update failed:', error);
            Swal.fire('Error', 'Failed to update question.', 'error');
        }
    };

    const saveEdit = async () => {
        try {
            await api.put(`/questions/${editData.id}`, editData);
            setQuestions(prev =>
                prev.map(q => q.id === editData.id ? { ...q, ...editData } : q)
            );
            setEditModalOpen(false);
            setToastMessage('Question updated');
            setToastOpen(true);
        } catch {
            Swal.fire('Error', 'Update failed.', 'error');
        }
    };

    const addQuestion = async () => {
        try {
            const res = await api.post('/questions', editData);
            setQuestions(prev => [...prev, res.data]);
            setAddModalOpen(false);
            setToastMessage('Question added');
            setToastOpen(true);
        } catch {
            Swal.fire('Error', 'Add failed.', 'error');
        }
    };

    useEffect(() => {
        fetchQuestions();
    }, []);

    const columns = [
        { field: 'id', headerName: 'ID', width: 70 },
        { field: 'questionText', headerName: 'Question', flex: 2 },
        { field: 'correctOption', headerName: 'Correct Option', width: 150 },
        { field: 'questionType', headerName: 'Type', width: 180 },
        { field: 'marks', headerName: 'Marks', width: 100 },
        { field: 'status', headerName: 'Status', flex: 1 },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <Box display="flex" gap={1}>
                    <Switch
                        checked={params.row.status === 'Active'}
                        onChange={() => handleStatusToggle(params.row.id, params.row.status)}
                        color="primary"
                    />
                    <Tooltip title="View">
                        <IconButton color="info"><FiEye /></IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton color="primary" onClick={() => handleEdit(params.row)}>
                            <FiEdit />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
                            <FiTrash2 />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    const CustomToolbar = () => (
        <GridToolbarContainer>
            <GridToolbarExport />
        </GridToolbarContainer>
    );

    return (
        <Box display="flex" flexDirection="column"
            minHeight="100%"
            p={isSmall ? 1 : 2}>
            {/* Header */}
            <Box mb={3} display="flex"
                flexDirection={isSmall ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isSmall ? 'stretch' : 'center'}
                gap={2}>
                <IconButton onClick={() => navigate(-1)}
                    sx={{
                        borderRadius: '12px',
                        p: '8px 12px',
                        background: 'linear-gradient(135deg, #00bcd4, #007bff)',
                        color: '#fff',
                        alignSelf: isSmall ? 'flex-start' : 'center',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #007bff, #00bcd4)',
                            transform: 'scale(1.05)'
                        }
                    }}>
                    <FiArrowLeft />
                    {!isSmall && <Typography sx={{ ml: 1, color: '#fff' }}>Back</Typography>}
                </IconButton>

                <Box display="flex" alignItems="center" gap={1}>
                    <Box sx={{
                        fontSize: '2rem',
                        background: '#e3f2fd',
                        borderRadius: '50%',
                        p: 1,
                        color: '#1976d2',
                        animation: 'pulseIcon 1.6s infinite',
                    }}>
                        <FiHelpCircle />
                    </Box>
                    <Typography
                        variant={isSmall ? 'h6' : 'h5'}
                        sx={{
                            fontWeight: 'bold',
                            background: 'linear-gradient(90deg, #007BFF, #00d4ff)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            textTransform: 'uppercase',
                            letterSpacing: '1.2px',
                        }}
                    >
                        Manage Questions
                    </Typography>
                </Box>

                <Button
                    variant="contained"
                    color="warning"
                    startIcon={<FiPlus />}
                    onClick={() => setIsAddModalOpen(true)}
                >
                    Add Question
                </Button>
            </Box>

            {/* DataGrid */}
            <Box flexGrow={1} overflow="auto">
                {loading ? (
                    <Box display="flex"
                        justifyContent="center"
                        alignItems="center" height="100%">
                        <CircularProgress />
                    </Box>
                ) : (
                    <DataGrid
                        rows={questions}
                        columns={columns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        pagination
                        components={{ Toolbar: CustomToolbar }}
                        loading={loading}
                        initialState={{
                            pagination: {
                                paginationModel: { pageSize: 25, page: 0 },
                            },
                        }}
                        getRowId={(row) => row.id}
                        sx={{
                            fontFamily: 'Segoe UI',
                            border: '1px solid #ddd',
                            boxShadow: 2,
                            '& .MuiDataGrid-columnHeaders': {
                                background: 'linear-gradient(to right, #e0f3ff, #d5faff)',
                                color: '#004c99',
                                fontWeight: 'bold',
                                fontSize: '1rem'
                            },
                            '& .MuiDataGrid-row:hover': {
                                backgroundColor: '#fef7e0'
                            },
                            '& .MuiDataGrid-cell': {
                                fontSize: '0.95rem'
                            }
                        }}
                    />
                )}
            </Box>

            <QuestionFormModal
                isOpen={isAddModalOpen}
                onClose={() => setIsAddModalOpen(false)}
                onSubmit={handleAddQuestion}
                editingQuestion={editingQuestion}
            />

            <EditQuestionFormModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                editingQuestion={editingQuestion}
                onSubmit={handleEditQuestion}
            />



            {/* Edit Modal */}
            <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Edit Question</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        label="Question Text"
                        margin="dense"
                        value={editData.questionText}
                        onChange={e => setEditData({ ...editData, questionText: e.target.value })}
                    />
                    <Select
                        fullWidth
                        margin="dense"
                        value={editData.correctOption}
                        onChange={e => setEditData({ ...editData, correctOption: e.target.value })}
                    >
                        <MenuItem value="A">A</MenuItem>
                        <MenuItem value="B">B</MenuItem>
                        <MenuItem value="C">C</MenuItem>
                        <MenuItem value="D">D</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="primary" onClick={saveEdit}>Save</Button>
                </DialogActions>
            </Dialog>

            {/* Add Modal */}
            {/* <Dialog open={addModalOpen} onClose={() => setAddModalOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Add Question</DialogTitle>
                <DialogContent dividers>
                    <TextField
                        fullWidth
                        label="Question Text"
                        margin="dense"
                        value={editData.questionText}
                        onChange={e => setEditData({ ...editData, questionText: e.target.value })}
                    />
                    <Select
                        fullWidth
                        margin="dense"
                        value={editData.correctOption}
                        onChange={e => setEditData({ ...editData, correctOption: e.target.value })}
                    >
                        <MenuItem value="A">A</MenuItem>
                        <MenuItem value="B">B</MenuItem>
                        <MenuItem value="C">C</MenuItem>
                        <MenuItem value="D">D</MenuItem>
                    </Select>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAddModalOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={addQuestion}>Add</Button>
                </DialogActions>
            </Dialog> */}

            {/* Toast */}
            <Snackbar
                open={toastOpen}
                autoHideDuration={3000}
                onClose={() => setToastOpen(false)}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <Alert severity="success" sx={{ width: '100%' }}>
                    {toastMessage}
                </Alert>
            </Snackbar>

            {/* Animation */}
            <style>{`
        @keyframes pulseIcon {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.1); opacity: 0.7; }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
        </Box>
    );

};

export default QuestionList;
